import React, { Component } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import axios from "axios";
import MapPage from "./MapPage";
import Sidebar from "./Sidebar";
import HeaderBar from "./HeaderBar";
import { getPolConfig, getPolColours } from "./utils/polAggregators";
import { buildLabelsAndColors } from "./utils/colorScale";
import { DEMO_LABELS } from "./utils/demographicLabels";
import { loadGeo } from "./utils/mapPage/loadGeo";

const SIDEBAR_WIDTH = 420;
const SIDEBAR_COLLAPSED = 28;

const THEME_CONFIGS = {
  explore: { label: "Explore" },
};

class MainPage extends Component {
  constructor(props) {
    super(props);

    const { groupsById } = getPolConfig();
    const theme_colours = getPolColours();
    const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);

    this.state = {
      isPanelOpen: true,
      selectedSection: "explore",
      user: { email: "", name: "", company: "" },
      isDemoUser: false,

      groupsById,
      theme_colours,
      colorLabels: labels,
      colorMap: colors,

      selectedVariable: "gov_performance",
      preferenceFilter: "top",

      geoLevel: "electorate",
      PossibleAreas: [],
      selectedFeature: null,
      selectedFeatureData: null,
      selectedConstName: null,
      selectedAreaIdToZoom: null,
      rankedItems: [],

      gender: null,
      age: null,
      ethnicity: null,
      party: null,
      education: null,

      waveSelect: null,
      waveOptions: [],
      compareWaves: false,
      showArrows: true,
      compareKey: null,

      hoverArea: null,

      ActionMode: "explore",
      ActionTargetVariables: { TargetVariable1: "fonterra_familiarity" },
      ActionTargetSubVariables: { TargetSubVariable1: "fonterra_familiar_very_familiar" },
      ActionTargetConcentration: "all",
    };
  }

  componentDidMount() {
    axios.get("/api/user-info/")
      .then((res) => {
        const user = res.data;
        if (!user || Object.keys(user).length === 0) {
          window.location.href = "/";
          return;
        }
        this.setState({ user, isDemoUser: Boolean(user?.is_demo_user) });
      })
      .catch(() => { window.location.href = "/"; });

    loadGeo({
      level: "electorate",
      setState: () => {},
      mapRef: { current: null },
      backgroundOnly: false,
      updateAreas: true,
      handlePossibleAreasChange: (areas) => this.setState({ PossibleAreas: areas }),
    });
  }

  handleActionModeChange = (mode) => this.setState({ ActionMode: mode });

  handleActionTargetConcentrationChange = (val) =>
    this.setState({ ActionTargetConcentration: val });

  handleActionTargetVariableChange = ({ val, varId }) => {
    const group = this.state.groupsById?.[val];
    let subVar;
    switch (group?.type) {
      case "likert":
        subVar = group?.polarity ? "net" : group?.keys?.[0];
        break;
      case "likert_grid":
        subVar = "compare";
        break;
      case "price_elast":
        subVar = group?.polarity?.expect;
        break;
      default:
        subVar = group?.keys?.[0];
    }
    this.setState((prev) => ({
      ActionTargetVariables: {
        ...prev.ActionTargetVariables,
        [`TargetVariable${varId}`]: val,
      },
      ActionTargetSubVariables: {
        ...prev.ActionTargetSubVariables,
        [`TargetSubVariable${varId}`]: subVar,
      },
    }));
  };

  handleActionTargetSubVariableChange = ({ val, varId }) => {
    this.setState((prev) => ({
      ActionTargetSubVariables: {
        ...prev.ActionTargetSubVariables,
        [`TargetSubVariable${varId}`]: val,
      },
    }));
  };

  setThemeInUrl = (section) => {
    if (section !== this.state.selectedSection) {
      this.setState({ selectedSection: section });
    }
  };

  handleLogout = () => {
    axios.post("/api/user/logout/").finally(() => { window.location.href = "/"; });
  };

  handleRankedUpdate   = (items) => this.setState({ rankedItems: items });
  handleAreaSelect     = (area)  => this.setState({ selectedAreaIdToZoom: area?.id ?? null });
  handlePossibleAreasChange = (areas) => this.setState({ PossibleAreas: areas });
  handleFeatureSelect  = ({ feature, data, selectedConstName }) =>
    this.setState({
      selectedFeature: feature || null,
      selectedFeatureData: data || null,
      selectedConstName: selectedConstName ?? null,
    });

  handleVariableChange = (val) => {
    const group = this.state.groupsById[val];
    const waves = group?.waves;
    const latestWave = waves ? [...waves].sort((a, b) => b - a)[0] : null;
    const defaultFilter = group?.type === "likert" ? "net" : "top";
    this.setState({
      selectedVariable: val,
      preferenceFilter: defaultFilter,
      ...(latestWave != null ? { waveSelect: latestWave, waveOptions: waves } : {}),
    });
  };

  handleGeoLevelChange = (val) => {
    this.setState({ geoLevel: val, selectedFeature: null, selectedAreaIdToZoom: null });
  };

  togglePanel = () => {
    this.setState((s) => ({ isPanelOpen: !s.isPanelOpen }), () => {
      let elapsed = 0;
      const iv = setInterval(() => {
        window.dispatchEvent(new Event("resize"));
        elapsed += 50;
        if (elapsed >= 350) clearInterval(iv);
      }, 50);
    });
  };

  render() {
    const {
      isPanelOpen, selectedSection, selectedVariable, preferenceFilter,
      groupsById, theme_colours, geoLevel, PossibleAreas,
      gender, age, ethnicity, party, education,
      rankedItems, hoverArea, waveSelect, waveOptions,
      compareWaves, showArrows, compareKey, selectedAreaIdToZoom, user,
      ActionMode, selectedFeature,
      ActionTargetVariables, ActionTargetSubVariables, ActionTargetConcentration,
    } = this.state;

    const selectedDemographics = {
      gender:    gender    ? DEMO_LABELS.gender[gender]       : null,
      age:       age       ? DEMO_LABELS.age[age]             : null,
      ethnicity: ethnicity ? DEMO_LABELS.ethnicity[ethnicity] : null,
      party:     party     ? DEMO_LABELS.party[party]         : null,
      education: education ? DEMO_LABELS.education[education] : null,
    };

    return (
      <Box sx={{ display: "flex", height: "100vh", flexDirection: "column" }}>
        <HeaderBar
          THEME_CONFIGS={THEME_CONFIGS}
          selectedSection={selectedSection}
          setThemeInUrl={this.setThemeInUrl}
          user={user}
          onLogout={this.handleLogout}
        />

        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, position: "relative" }}>
            <MapPage
              selectedGroupId={selectedVariable}
              preferenceFilter={preferenceFilter}
              groupsById={groupsById}
              theme_colours={theme_colours}
              geoLevel={geoLevel}
              onGeoLevelChange={this.handleGeoLevelChange}
              genderFilter={gender}
              ageFilter={age}
              ethnicityFilter={ethnicity}
              partyFilter={party}
              educationFilter={education}
              selectedDemographics={selectedDemographics}
              onResetDemographics={() => this.setState({ gender: null, age: null, ethnicity: null, party: null, education: null })}
              onFeatureSelect={this.handleFeatureSelect}
              onRankedUpdate={this.handleRankedUpdate}
              handlePossibleAreasChange={this.handlePossibleAreasChange}
              PossibleAreas={PossibleAreas}
              onAreaSelect={this.handleAreaSelect}
              selectedAreaIdToZoom={selectedAreaIdToZoom}
              hoverArea={hoverArea}
              handlehoverAreaChange={(v) => this.setState({ hoverArea: v })}
              waveSelect={waveSelect}
              waveOptions={waveOptions}
              handlewaveSelectChange={(v) => this.setState({ waveSelect: v })}
              compareWaves={compareWaves}
              onCompareWavesChange={(v) => this.setState({ compareWaves: v })}
              showArrows={showArrows}
              onShowArrowsToggle={() => this.setState((s) => ({ showArrows: !s.showArrows }))}
              compareKey={compareKey}
              onCompareKeyChange={(v) => this.setState({ compareKey: v })}
              ActionMode={ActionMode}
              ActionTargetVariables={ActionTargetVariables}
              ActionTargetSubVariables={ActionTargetSubVariables}
              ActionTargetConcentration={ActionTargetConcentration}
            />
          </Box>

          <Box
            sx={{
              width: isPanelOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED,
              flexShrink: 0,
              height: "100%",
              overflow: "hidden",
              zIndex: 1200,
              bgcolor: "#fff",
              transition: "width 0.2s ease",
            }}
          >
            <Sidebar
              isPanelOpen={isPanelOpen}
              togglePanel={this.togglePanel}
              sectionTitle="Electorate Profiles"
              groupsById={groupsById}
              selectedVariable={selectedVariable}
              onVariableChange={this.handleVariableChange}
              preferenceFilter={preferenceFilter}
              onPreferenceFilterChange={(v) => this.setState({ preferenceFilter: v })}
              genderFilter={gender}       onGenderChange={(v) => this.setState({ gender: v })}
              ageFilter={age}             onAgeChange={(v) => this.setState({ age: v })}
              ethnicityFilter={ethnicity} onEthnicityChange={(v) => this.setState({ ethnicity: v })}
              partyFilter={party}         onPartyChange={(v) => this.setState({ party: v })}
              educationFilter={education} onEducationChange={(v) => this.setState({ education: v })}
              onClearAll={() => this.setState({ gender: null, age: null, ethnicity: null, party: null, education: null })}
              rankedItems={rankedItems}
              onAreaSelect={this.handleAreaSelect}
              hoverArea={hoverArea}
              handlehoverAreaChange={(v) => this.setState({ hoverArea: v })}
              compareWaves={compareWaves}
              user={user}
              ActionMode={ActionMode}
              handleActionModeChange={this.handleActionModeChange}
              selectedFeature={selectedFeature}
              selectedFeatureData={this.state.selectedFeatureData}
              selectedConstName={this.state.selectedConstName}
              selectedDemographics={selectedDemographics}
              ActionTargetVariables={ActionTargetVariables}
              ActionTargetSubVariables={ActionTargetSubVariables}
              ActionTargetConcentration={ActionTargetConcentration}
              onActionTargetVariableChange={this.handleActionTargetVariableChange}
              handleActionTargetSubVariableChange={this.handleActionTargetSubVariableChange}
              handleActionTargetConcentrationChange={this.handleActionTargetConcentrationChange}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

function MainPageWithParams(props) {
  const { slug } = useParams();
  const navigate = useNavigate();
  return <MainPage {...props} slug={slug} navigate={navigate} />;
}

export default MainPageWithParams;
